
import { PaymentType } from './payment';
import { DeliveryType, OrderStatus } from "./order";
import { Translation } from './tranlsation';

export interface ICompany {
  companyID: number;
  name: string;
  facilityName: string;
  description: Translation[];
  address: string;
  lastKnownIPAddress: string;
  phone: string;
  menuDate: string;
  ownerID: number;
  isPublic: boolean;
  created: Date;
  zipCode: string;
  city: string;
  taxNo: string;
  ownerName: string;
  identificationNo: string;
  eMail: string;
  posName: string;
  deleted: boolean;
  images: CompanyImage[];
  referenceID: string | null;
  organizationID?: number;
}

export interface WiFi {
  ssid: string;
  password: string;
  hidden: boolean;
  authentication: string;
}

export interface CompanyAdminSettings {
  allowMenuModification: boolean;
  allowGlovo: boolean;
  allowMrD: boolean;
  allowWolt: boolean;
  allowGloriafood: boolean;
  allowPBX: boolean;
  eMenuPro: boolean;
  disabled: boolean;
  allowItemAvailabilityChange: boolean;

  personalizedUserApp: boolean;
  userAppName: string;
  userAppShortName: string;

  customerDisplayConcurrencyLimit: number;
  kitchenDisplayConcurrencyLimit: number;
  waiterAppConcurrencyLimit: number;
  waiterAppProConcurrencyLimit: number;
  kioskConcurrencyLimit: number;
  digitalSignageConcurrencyLimit: number;
  allowUserApp: boolean;
  allowUserAppPro: boolean;
  allowTableStar: boolean;

  server: string;

  sync_AllowModifierModification: boolean;
  sync_AllowSuggestionModification: boolean;
  sync_AllowGroupModification: boolean;

  /**
   * Per-firma override za billing enforcement.
   * Ako globalni AppSettings.EnableConcurrencyLimits = true, primenjuje se na sve firme (ovo polje se ignoriše).
   * Ako globalni = false, gleda se ovaj per-firma flag.
   */
  enableConcurrencyLimits: boolean;

  /**
   * Per-firma enforcement samo za navedene servise — CSV Billing kodova (npr. "EBAR_ORDERING,WOLT").
   * Ti servisi traže pretplatu i kad je enableConcurrencyLimits isključen; ostali ostaju otvoreni.
   */
  enforcedServiceCodes?: string;

  // Default verzija Angular aplikacije na nivou firme, po aplikaciji. Prazno = bez firma-defaulta.
  waiterDefaultVersion: string;
  kitchenDisplayDefaultVersion: string;
  customerDisplayDefaultVersion: string;
  adminDefaultVersion: string;
  kioskDefaultVersion: string;
  userAppDefaultVersion: string;
}
/** Kako se order-level popust prikazuje na fiskalnom (V-PFR) računu. Mora se poklapati sa backend enumom. */
export enum FiscalDiscountMode {
  /** Proporcionalno na sve stavke (snižene cene). */
  Proportional = 0,
  /** Popust kao zasebno „druga bezgotovinska" plaćanje. */
  OtherPayment = 1,
}

export interface CompanySettings extends CompanyAdminSettings {
  allowedIP: string;
  allowOnlyLocalIP: boolean;
  WiFi: WiFi;
  /** @deprecated OBSOLETE — globalna lista; zamenjuju je per-app liste ispod. Ostaje kao
   *  fallback (null per-app = nasledi ovu) i u payload-u za stare verzije aplikacija. */
  allowedDeliveryType: DeliveryType[];
  /** Per-app lista za kiosk (EatIn/Pickup). null = nasledi allowedDeliveryType (filtriran na EatIn/Pickup). */
  kiosk_AllowedDeliveryType?: DeliveryType[] | null;
  /** Per-app lista za korisničku aplikaciju. null = nasledi allowedDeliveryType. */
  userApp_AllowedDeliveryType?: DeliveryType[] | null;
  /** Per-app lista za konobarsku aplikaciju (direktne porudžbine bez stola). null = nasledi allowedDeliveryType. */
  waiter_AllowedDeliveryType?: DeliveryType[] | null;
  allowTableTotal: boolean;
  allowTableTotalWithoutOrder: boolean;
  allowCallWaiter: boolean;
  requestConfirmationForCallWaiter: boolean;
  allowedOrderStatusNotifications: OrderStatus[];
  /** Praćenje statusa kiosk porudžbine preko QR koda (javna /track strana). [Public] setting;
   *  push notifikacije sa te strane koriste statuse iz allowedOrderStatusNotifications. */
  orderTrackingEnabled?: boolean;
  /** Prikaz pozicije vozača kupcu na /track strani (dok je "na dostavi"). Default true; firma može isključiti. */
  showDeliveryTrackingToCustomer?: boolean;
  allowedOrderStatus: OrderStatus[];
  requireAuthenticatedUserForOrder: boolean;
  requireAuthenticatedUserForView: boolean;
  allowedUsers: string[];
  blockedUsers: string[];
  allowPaymentRequest: boolean;
  requestConfirmationForPaymentRequest: boolean;
  showTimeLimitedProducts: boolean;
  /** Prikaži proizvode koji trenutno nisu dostupni u lokalu, sa oznakom „Trenutno nije dostupno“. */
  showUnavailableProducts: boolean;
  requirePOSReponse: boolean;
  allowChargeOnWaiterApp: boolean;
  showOrderNoOnWaiterApp: boolean;
  allowWaiterDebtInfoInWaiterApp: boolean;
  requireDeliveryInfoForPickupInWaiterApp: boolean;
  /** „Izdaj porudžbinu“ u konobarskoj aplikaciji prvo prikazuje predloge (kao kiosk) na osnovu stavki porudžbine.
   *  Opciono: stariji server (ili LocalServer sa starim kešom) ga ne šalje — tada se predlozi ne prikazuju. */
  showSuggestionsInWaiterApp?: boolean;
  groupOrdersByGroupInUserApp: boolean;
  numberOfGroupsInTable: number;
  repeatSoundForPendingNotifications: boolean;
  requireAuthenticatedUserForPickaupAndDelivery: boolean;
  alowChargeInOrderDetails: boolean;
  disableMultipleEmployeeSessions: boolean;
  sendOrderPreparationTime: boolean;
  allowedPaymentTypes: PaymentType[];
  autoShowTableTotal: boolean;
  showPictures: boolean;
  showOnlyCompanyLogo: boolean;

  mrDEnabled: boolean;
  mrDUserName: string;
  mrDPassword: string;

  woltEnabled: boolean;
  woltAPIKey: string;
  woltVenueID: string;
  woltSecret: string;
  woltChangeOrderStatus: boolean;
  woltChangeProductStatus: boolean;
  woltUsername: string;
  woltPassword: string;
  wolt_OrderPaidStatus: OrderStatus;
  woltEmployeeID?: number | null;

  glovoEnabled: boolean;
  glovoStoreID: string;
  glovoChangeOrderStatus: boolean;
  glovoEmployeeID?: number | null;

  /** Kod online provajdera kartične naplate (npr. "RAIACCEPT") ili prazno = bez online naplate. */
  onlinePaymentProvider?: string | null;
  /** Rok upozorenja (min) za online porudžbinu koja čeka prihvatanje. */
  onlinePayment_AcceptWarnMinutes?: number;
  /** Rok (min) posle kog se rezervisana porudžbina otkazuje i rezervacija oslobađa. */
  onlinePayment_AcceptCancelMinutes?: number;
  /** Vrste uređaja koje znače „restoran je povezan" (kd,pos,w), zarezom odvojene; prazno = sve. */
  onlinePayment_RequiredDeviceTypes?: string | null;

  hideItemPriceInNewOrderInWaiterApp: boolean;
  disableChargeWithPendingOrderInWaiterApp: boolean;

  ordersDisplayPendingWidth: number;
  ordersDisplayReadyWidth: number;
  ordersDisplay_ShowTimer: boolean;
  ordersDisplay_HiddenProviders: string;

  primaryColor: string;
  gpsLocation: GPSLocation;
  availableLanguages: string[];

  pbxEnabled: boolean
  pbxKey: string;
  pbxPhoneNumberPattern: string;

  gloriafood_Enabled: boolean;
  gloriafood_RestaurantKey: string;
  gloriafood_MasterKey: string;
  gloriafood_NewOrderStatus: OrderStatus;
  gloriafood_EmployeeID?: number | null;

  requireDeliveryInfoForPickupInUserApp: boolean;

  allowExternalProvider: boolean;
  externalProvider_Enabled: boolean;
  externalProvider_Key: string;
  externalProvider_Name: string;
  externalProvider_WebhookURL: string;
  externalProvider_OrderPaidStatus: OrderStatus

  allowTookan: boolean;
  tookan_Key: string;
  tookan_Enabled: boolean;

  parentCompanyID?: number;

  requestConfirmationForOrderItemStatusChange: boolean;
  showPayedInstantOrders: boolean;

  workingHours: string;
  businessDayStartHour: number;
  facebookURL: string;
  googlePlaceID: string;
  googleMapsURL: string;
  instagramURL: string;
  twitterURL: string;
  tripAdvisorURL: string;

  currency: string;
  defaultLanguage: string;
  /** ISO 3166-1 alpha-2 kod države firme (npr. "RS"). Podrazumevano "RS" (Srbija). Vozi i default pozivni broj. */
  country: string;

  /** Kako se popust prikazuje pri backend fiskalizaciji (V-PFR). Podrazumevano proporcionalno. */
  fiscalDiscountMode?: FiscalDiscountMode;

  pinToken: string;

  tableStarApiKey: string;

  eGazdaUserName: string;
  eGazdaPassword: string;

  dueDateSelectorType: DueDateSelectorType;
  /** @deprecated OBSOLETE — direktne porudzbine sada zavise od waiter_AllowedDeliveryType
   *  (bar jedan nacin dostave). Polje se i dalje isporucuje zbog starih verzija konobarske app. */
  waiter_ShowDirectOrders: boolean;
  waiter_SendOrderReadyNotification: boolean;

  kiosk_AllowOrderWithoutPayment: boolean;

  printRatingBarcode: boolean;

  orderPaidStatus: OrderStatus;
  /** Režim prikaza proizvoda u korisničkoj aplikaciji (full | mixed | compact; prazno = automatski).
   *  Uprkos imenu NIJE šema boja — za boje v. `userApp_ColorTheme`. */
  userAppTheme: string;
  /** Tema korisničke aplikacije za lokal: prazno = podrazumevana šema bez org CSS-a, ugrađena šema
   *  ili `org:<id>` teme organizacije (v. `organizationCssThemes`). */
  userApp_ColorTheme?: string | null;

  deliveryProductID?: number | null;

  /** Regioni dostave (krug/poligon) sa cenom i pragom besplatne dostave. */
  deliveryRegions?: DeliveryRegion[] | null;

  changeOrderStatusOnItemStatusChange: boolean;

  useOrganizationMenu: boolean;

  /** Broj lokala u organizaciji kojoj firma pripada; 1 kad firma nije u organizaciji.
   *  Sa jednim lokalom razlika „firma / lokal" nema koga da razdvoji, pa portal spaja parove
   *  stavki (Meni + Ponuda lokala, Zaposleni ×2, Podešavanja + Firma) i prikazuje JEDAN prekidač
   *  dostupnosti — server tada i drži sve u glavnoj tabeli menija (v. docs/nav-reorg.md §14).
   *  Izostavljeno (stariji server) = ponašaj se kao da ih ima više, tj. ne spajaj ništa. */
  organizationCompanyCount?: number;

  closed: boolean;
  userApp_EnablePayment: boolean;
  userApp_AllowOrderWithoutPayment: boolean;
  /** Da li kupac sme da zakaže Pickup/Delivery porudžbinu (izbor budućeg vremena gotovosti). */
  userApp_AllowScheduledOrders: boolean;
  /** Maksimalan broj dana unapred (ne računajući danas) za zakazivanje porudžbine. */
  userApp_MaxScheduleDaysInAdvance: number;

  /** Katalog tema organizacije — šta uređaj sme da izabere u podešavanju `theme` kao `org:<id>`.
   *  Stavka nosi šemu boja i verziju (hash) CSS-a, NE i sam CSS: OrgThemeService od verzije gradi
   *  /api/organization/css/{companyID}/{version}.css i učitava ga kao <link id="org-custom-css">.
   *  Izostavljeno = ne diraj ništa (stariji server); prazan niz = organizacija nema tema. */
  organizationCssThemes?: OrgCssThemeEntry[] | null;
}

/** Jedna tema organizacije u katalogu (`CompanySettings.organizationCssThemes`). */
export interface OrgCssThemeEntry {
  id: number;
  name: string;
  /** Ugrađena šema koju tema nadograđuje: plum | daylight | blue | contrast | warm. */
  scheme: string;
  /** Hash CSS-a; prazno = tema bez CSS-a ili je org CSS isključen kill-switch-om. */
  version: string;
}

export enum ImageType {
  logo = "logo",
  background = "background",
  QRCodeLogo = "QRCodeLogo",
  userAppIcon = "userAppIcon",
  /**
   * @deprecated OBSOLETE — slika dugmeta „za ovde" na kiosku. Upload je uklonjen iz admin
   * portala; zamenjuje je org CSS token `--app-icon-eatin`
   * (v. WebKioskApp/docs/customization.md). Vrednost ostaje jer je stare verzije kioska
   * još čitaju; briše se kad te verzije izađu iz upotrebe.
   */
  eatIn = "eatInImage",
  /**
   * @deprecated OBSOLETE — slika dugmeta „za poneti"; zamenjuje je org CSS token
   * `--app-icon-pickup`. V. napomenu uz `eatIn`.
   */
  pickup = "pickupImage",
  printImage = "printImage",
  pwaIcon72 = "pwaIcon72",
  pwaIcon96 = "pwaIcon96",
  pwaIcon128 = "pwaIcon128",
  pwaIcon144 = "pwaIcon144",
  pwaIcon152 = "pwaIcon152",
  pwaIcon192 = "pwaIcon192",
  pwaIcon384 = "pwaIcon384",
  pwaIcon512 = "pwaIcon512",
}
export interface CompanyImage {
  type: ImageType;
  hash: string | null;
  url?: string;
}

export function createCompany(): ICompany {
  return {
    companyID: 0,
    name: '',
    facilityName: '',
    description: [],
    address: '',
    lastKnownIPAddress: '',
    phone: '',
    menuDate: '',
    ownerID: 0,
    isPublic: false,
    created: new Date(),
    zipCode: '',
    city: '',
    taxNo: '',
    ownerName: '',
    identificationNo: '',
    eMail: '',
    posName: 'POS',
    deleted: false,
    images: [],
    referenceID: ''
  }
}

export interface GPSLocation {
  lng: number;
  lat: number;
}

/**
 * Region dostave (krug ili poligon) koji vlasnik lokala crta na mapi.
 * Čuva se kao JSON u okviru CompanySettings.deliveryRegions. Za GPS tačku u više
 * regiona uzima se najmanja deliveryPrice.
 */
export interface DeliveryRegion {
  /** Stabilan identifikator (GUID string generisan u UI-ju). */
  id: string;
  name: string;
  /** "circle" ili "polygon". */
  type: 'circle' | 'polygon';
  /** Centar kruga (samo type === 'circle'). */
  center?: GPSLocation | null;
  /** Poluprečnik kruga u metrima (samo type === 'circle'). */
  radiusMeters?: number | null;
  /** Tačke poligona (samo type === 'polygon'). */
  path?: GPSLocation[] | null;
  deliveryPrice: number;
  /** Minimalni iznos porudžbine iznad kog je dostava besplatna (null = uvek se naplaćuje). */
  freeDeliveryThreshold?: number | null;
}


export enum DueDateSelectorType {
  Minutes = 0,
  Days = 1
}
